import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronRight, Shield, Lock, UserX, LogOut, Wrench, Wallet2, HelpCircle, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";
import BottomNav from "@/components/BottomNav";

function Toggle({ on, onClick, testId }) {
  return (
    <button data-testid={testId} onClick={onClick}
      className={`w-10 rounded-full p-0.5 transition-colors ${on ? "gradient-primary" : "bg-white/10"}`} style={{ height: 22 }}>
      <span className={`block bg-white rounded-full transition-transform ${on ? "translate-x-5" : ""}`} style={{ width: 18, height: 18 }} />
    </button>
  );
}

function Row({ icon: Icon, label, right, onClick, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} className="w-full flex items-center gap-3 px-4 py-3.5 active:bg-white/5 transition-colors">
      <Icon size={16} className="text-[#E6B84C] shrink-0" />
      <span className="flex-1 text-left text-xs font-semibold text-zinc-200">{label}</span>
      {right || <ChevronRight size={14} className="text-zinc-600" />}
    </button>
  );
}

export default function Settings() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [notif, setNotif] = useState(localStorage.getItem("yaara_notif") !== "off");
  const [saver, setSaver] = useState(localStorage.getItem("yaara_saver") === "on");
  const [lang, setLang] = useState(localStorage.getItem("yaara_lang") || "English");
  const [quality, setQuality] = useState(localStorage.getItem("yaara_quality") || "1080p");

  const soon = () => toast("Coming soon ✨");

  return (
    <div className="min-h-screen max-w-md mx-auto px-4 py-6 pb-28" data-testid="settings-page">
      <div className="flex items-center justify-between mb-5">
        <button data-testid="settings-back" onClick={() => navigate(-1)} className="text-[#E6B84C]"><ArrowLeft size={20} /></button>
        <h1 className="font-royal text-2xl gradient-gold-text font-bold tracking-wide">SETTINGS</h1>
        <span className="w-5" />
      </div>

      <div data-testid="settings-profile-card" role="button" tabIndex={0} onClick={() => navigate(`/profile/${user?.id}`)}
        onKeyDown={(e) => e.key === "Enter" && navigate(`/profile/${user?.id}`)}
        className="w-full glass rounded-2xl px-4 py-3.5 flex items-center gap-3 mb-6 active:scale-[0.99] transition-transform cursor-pointer">
        <Avatar src={user?.avatar} size={48} tier={user?.tier} />
        <div className="flex-1 text-left">
          <p className="text-white font-bold text-sm">{user?.name} <span className="text-[#3B82F6]">✔</span></p>
          <p className="text-[10px] text-zinc-500">Edit profile ›</p>
        </div>
        <button data-testid="settings-level-pill" onClick={(e) => { e.stopPropagation(); navigate("/level"); }}
          className="text-[10px] font-extrabold px-3 py-1 rounded-full bg-[#E6B84C] text-[#0B0F19]">👑 Lv. {user?.level}</button>
      </div>

      <p className="text-[10px] font-extrabold text-zinc-600 tracking-widest mb-1.5 px-1">ACCOUNT</p>
      <div className="glass rounded-2xl mb-5 divide-y divide-white/5">
        <Row icon={Shield} label="Account & Security" onClick={soon} testId="settings-security" />
        <Row icon={Lock} label="Privacy" onClick={soon} testId="settings-privacy" />
        <Row icon={UserX} label="Blocked Users" onClick={soon} testId="settings-blocked" />
      </div>

      <p className="text-[10px] font-extrabold text-zinc-600 tracking-widest mb-1.5 px-1">PREFERENCES</p>
      <div className="glass rounded-2xl mb-5 divide-y divide-white/5">
        <div className="flex items-center gap-3 px-4 py-3.5">
          <span className="text-[#E6B84C] text-sm">🔔</span>
          <span className="flex-1 text-xs font-semibold text-zinc-200">Notifications</span>
          <Toggle on={notif} testId="settings-notif-toggle" onClick={() => { const v = !notif; setNotif(v); localStorage.setItem("yaara_notif", v ? "on" : "off"); }} />
        </div>
        <div className="flex items-center gap-3 px-4 py-3.5">
          <span className="text-[#E6B84C] text-sm">🌐</span>
          <span className="flex-1 text-xs font-semibold text-zinc-200">Language</span>
          <button data-testid="settings-language" onClick={() => { const v = lang === "English" ? "हिंदी" : "English"; setLang(v); localStorage.setItem("yaara_lang", v); toast(v === "English" ? "Language: English" : "भाषा: हिंदी (full translation coming soon)"); }}
            className="text-[10px] font-bold text-[#E6B84C] bg-[#E6B84C]/10 border border-[#E6B84C]/30 px-3 py-1 rounded-full">{lang === "English" ? "English / हिंदी" : "हिंदी / English"}</button>
        </div>
        <div className="flex items-center gap-3 px-4 py-3.5">
          <span className="text-[#E6B84C] text-sm">🌙</span>
          <span className="flex-1 text-xs font-semibold text-zinc-200">Dark Theme</span>
          <Toggle on={true} testId="settings-dark-toggle" onClick={() => toast("Royal dark theme is signature Yaara ✨")} />
        </div>
        <div className="flex items-center gap-3 px-4 py-3.5">
          <span className="text-[#E6B84C] text-sm">📶</span>
          <span className="flex-1 text-xs font-semibold text-zinc-200">Data Saver</span>
          <Toggle on={saver} testId="settings-saver-toggle" onClick={() => { const v = !saver; setSaver(v); localStorage.setItem("yaara_saver", v ? "on" : "off"); }} />
        </div>
      </div>

      <p className="text-[10px] font-extrabold text-zinc-600 tracking-widest mb-1.5 px-1">CREATOR</p>
      <div className="glass rounded-2xl mb-5 divide-y divide-white/5">
        <Row icon={Wrench} label="Creator Tools" onClick={() => navigate("/earnings")} testId="settings-creator-tools" />
        <Row icon={Wallet2} label="Payout Methods" onClick={() => navigate("/earnings")} testId="settings-payouts" />
        <div className="flex items-center gap-3 px-4 py-3.5">
          <span className="text-[#E6B84C] text-sm">📹</span>
          <span className="flex-1 text-xs font-semibold text-zinc-200">Live Quality</span>
          <button data-testid="settings-quality" onClick={() => { const v = quality === "1080p" ? "720p" : "1080p"; setQuality(v); localStorage.setItem("yaara_quality", v); }}
            className="text-[10px] font-bold text-[#E6B84C] bg-[#E6B84C]/10 border border-[#E6B84C]/30 px-3 py-1 rounded-full">{quality}</button>
        </div>
      </div>

      <p className="text-[10px] font-extrabold text-zinc-600 tracking-widest mb-1.5 px-1">SUPPORT</p>
      <div className="glass rounded-2xl mb-6 divide-y divide-white/5">
        <Row icon={HelpCircle} label="Help Centre" onClick={soon} testId="settings-help" />
        <Row icon={BookOpen} label="Community Rules" onClick={soon} testId="settings-rules" />
        <div className="flex items-center gap-3 px-4 py-3.5">
          <span className="text-[#E6B84C] text-sm">ℹ️</span>
          <span className="flex-1 text-xs font-semibold text-zinc-200">About Yaara</span>
          <span className="text-[10px] font-bold text-zinc-600">v4.2</span>
        </div>
      </div>

      <button data-testid="settings-logout" onClick={() => { logout(); navigate("/login"); }}
        className="w-full bg-gradient-to-r from-rose-600 via-[#FF007F] to-[#8A2BE2] text-white font-extrabold py-3.5 rounded-full flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
        <LogOut size={15} /> Log Out
      </button>
      <BottomNav />
    </div>
  );
}
